import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { PositionType } from 'data/types';

export function calculateAutoImageScale(
  naturalWidth: number,
  naturalHeight: number,
  totalSqft: number,
  levelCount: number
): number {
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    totalSqft <= 0 ||
    levelCount <= 0
  ) {
    return 1;
  }

  const perLevelSqft = totalSqft / levelCount;
  const scale =
    STRUCTURE_GRID_SIZE *
    Math.sqrt(perLevelSqft / (naturalWidth * naturalHeight));

  return Math.max(0.1, Math.min(5, scale));
}

export function computeLayoutImageGridCenter(
  naturalWidth: number,
  naturalHeight: number,
  startPositionX: number,
  startPositionY: number
): PositionType {
  const centerXPx = startPositionX + naturalWidth / 2;
  const centerYPx = startPositionY + naturalHeight / 2;
  return {
    x: Math.round(centerXPx / STRUCTURE_GRID_SIZE),
    y: Math.round(centerYPx / STRUCTURE_GRID_SIZE)
  };
}

export function computeLayoutImageGridDimensions(
  naturalWidth: number,
  naturalHeight: number,
  imageScale: number
): { width: number; height: number } {
  return {
    width: (naturalWidth * imageScale) / STRUCTURE_GRID_SIZE,
    height: (naturalHeight * imageScale) / STRUCTURE_GRID_SIZE
  };
}

export type LayoutImageMetrics = {
  center: PositionType;
  widthInGrid: number;
  heightInGrid: number;
};

export function computeLayoutImageMetrics(
  naturalWidth: number,
  naturalHeight: number,
  startPositionX: number,
  startPositionY: number,
  scale: number
): LayoutImageMetrics {
  const dims = computeLayoutImageGridDimensions(
    naturalWidth,
    naturalHeight,
    scale
  );
  return {
    center: computeLayoutImageGridCenter(
      naturalWidth,
      naturalHeight,
      startPositionX,
      startPositionY
    ),
    widthInGrid: dims.width,
    heightInGrid: dims.height
  };
}

export function loadImageDimensions(
  url: string
): Promise<{ naturalWidth: number; naturalHeight: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      });
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function resolveLayoutImageMetrics(
  imageInfo: {
    url: string;
    startPositionX: number;
    startPositionY: number;
    scale: number;
  } | null,
  cached: LayoutImageMetrics | null
): Promise<LayoutImageMetrics | null> {
  if (cached) return cached;
  if (!imageInfo) return null;

  const dims = await loadImageDimensions(imageInfo.url);
  if (!dims) return null;

  return computeLayoutImageMetrics(
    dims.naturalWidth,
    dims.naturalHeight,
    imageInfo.startPositionX,
    imageInfo.startPositionY,
    imageInfo.scale
  );
}

type ImageTransformLike = {
  scale?: number | null;
  rotation?: number | null;
  startPositionX?: number | null;
  startPositionY?: number | null;
};

export function computeScaledImageTransformAroundPivot(
  transform: ImageTransformLike,
  dims: { naturalWidth: number; naturalHeight: number },
  pivotInGrid: PositionType,
  ratio: number
): {
  scale: number;
  rotation: number;
  startPositionX: number;
  startPositionY: number;
} {
  const oldScale = transform.scale ?? 1;
  const oldPosX = transform.startPositionX ?? 0;
  const oldPosY = transform.startPositionY ?? 0;
  const oldCenterX = oldPosX + dims.naturalWidth / 2;
  const oldCenterY = oldPosY + dims.naturalHeight / 2;
  const pivotPxX = pivotInGrid.x * STRUCTURE_GRID_SIZE;
  const pivotPxY = pivotInGrid.y * STRUCTURE_GRID_SIZE;
  const newCenterX = pivotPxX + (oldCenterX - pivotPxX) * ratio;
  const newCenterY = pivotPxY + (oldCenterY - pivotPxY) * ratio;
  return {
    scale: oldScale * ratio,
    rotation: transform.rotation ?? 0,
    startPositionX: Math.round(newCenterX - dims.naturalWidth / 2),
    startPositionY: Math.round(newCenterY - dims.naturalHeight / 2)
  };
}
